import { useCallback, useEffect, useState } from "react";
import { MessageSquareReply, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { fmtDT, inputCls } from "../../lib/outreach";

const Bubble = ({ m }) => {
  const out = m.direction === "out";
  return (
    <div data-testid={`lead-message-${m.id}`} className={`rounded-xl border p-4 ${out ? "border-amber-500/30 bg-amber-500/5 sm:ml-10" : "border-slate-700 bg-[#0B1120]/70 sm:mr-10"}`}>
      <div className="flex flex-wrap justify-between gap-2 text-[11px] text-slate-400 mb-1.5">
        <span><span className={out ? "text-amber-300" : "text-sky-300"}>{out ? `You (${m.sent_by || m.from})` : m.from}</span> → {m.to}</span>
        <span>{fmtDT(m.at)}</span>
      </div>
      <p className="text-sm font-semibold text-white mb-2">{m.subject}</p>
      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-300 max-h-96 overflow-auto">{m.body}</pre>
    </div>
  );
};

const fillTemplate = (body, lead, settings) =>
  body
    .replaceAll("{first_name}", lead.first_name || (lead.contact_name || "").split(" ")[0] || "there")
    .replaceAll("{company}", lead.company_name || "your team")
    .replaceAll("{booking_link}", settings?.booking_url || "[booking link]");

export default function LeadRepliesThread({ lead, isAdmin, onReplied }) {
  const leadId = lead.id;
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => { if (isAdmin) api.get("/outreach/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, [isAdmin]);

  const load = useCallback(() => api.get(`/leads/${leadId}/messages`).then(({ data }) => setMsgs(data)).catch(() => {}), [leadId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (msgs.length && window.location.hash === "#replies") document.getElementById("replies")?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/messages/reply`, { body: text });
      toast.success("Reply sent from alex@kingofit.ca");
      setText("");
      await load();
      onReplied();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!msgs.length) return null;
  return (
    <div id="replies" data-testid="lead-replies-thread" className="mt-6">
      <h3 className="font-display font-bold text-base inline-flex items-center gap-2 mb-3"><MessageSquareReply className="w-5 h-5 text-sky-300" /> Replies ({msgs.filter((m) => m.direction === "in").length})</h3>
      <div className="space-y-3">{msgs.map((m) => <Bubble key={m.id} m={m} />)}</div>
      {isAdmin && (
        <form onSubmit={send} className="mt-4">
          {settings?.reply_templates?.length > 0 && (
            <div data-testid="reply-templates" className="flex flex-wrap gap-2 mb-2">
              {settings.reply_templates.map((t, i) => (
                <button key={t.name} type="button" data-testid={`reply-template-${i}`} onClick={() => setText(fillTemplate(t.body, lead, settings))}
                  className="rounded-full px-3 py-1 text-xs border border-slate-700 text-slate-300 hover:border-amber-500/60 hover:text-amber-300 transition-colors">
                  {t.name}
                </button>
              ))}
            </div>
          )}
          <textarea data-testid="lead-reply-input" required rows={5} className={inputCls} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your reply… (sent as plain text from alex@kingofit.ca in the same thread)" />
          <button data-testid="lead-reply-send-button" disabled={busy || !text.trim()} className="btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 mt-2 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send reply
          </button>
        </form>
      )}
    </div>
  );
}
